import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { Box, Button, Stack } from '@mui/material';
import { ClearOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'react-bootstrap';
import styles from './BlockSlot.module.css';
import useHover from '../../../hooks/UseHover';
import UseWindowSize from '../../../hooks/UseWindowSize';
import BlockSlotActionsButtonGroup from './BlockSlotActionsButtonGroup';

export enum BlockSlotPanelEventType {
  UP = 0,
  DOWN = 1,
  TOP = 2,
  BOTTOM = 3,
  DUPLICATE = 4,
  DELETE = 5,
}

export type BlockSlotPanelEvent = (id: string, event: BlockSlotPanelEventType) => void;

export enum AddPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
}

interface BlockSlotProps {
  children: React.ReactNode
  dataId: string
  defaultBackgroundColor?: string
  isEditing: boolean
  isHeader?: boolean
  isFooter?: boolean
  isStaticInBackground?: boolean
  isFullBleed?: boolean
  handlePanelEvent: BlockSlotPanelEvent;
  handleImageIconClicked?: () => void
  handleBlockAddClicked: (position: AddPosition) => void
  animation?: string,
  showOptionsForLines?: boolean,
  showOptionsForAlignment?: boolean,
  showOptionsForDisplaySettings?: boolean,
  handleLinesOptionsClicked?: () => void
  handleAlignmentOptionsClicked?: () => void
  handleDisplaySettingsClicked?: () => void
}

function BlockSlot({
  children,
  dataId,
  defaultBackgroundColor,
  isEditing,
  isHeader,
  isFooter,
  isStaticInBackground,
  isFullBleed,
  handlePanelEvent,
  handleImageIconClicked,
  handleBlockAddClicked,
  animation,
  showOptionsForLines = false,
  showOptionsForAlignment = false,
  showOptionsForDisplaySettings = false,
  handleLinesOptionsClicked,
  handleAlignmentOptionsClicked,
  handleDisplaySettingsClicked,
}: BlockSlotProps) {
  const { width } = UseWindowSize();
  const [realHoverRef, realIsHovered] = useHover<HTMLDivElement>();

  const [isInView, setIsInView] = useState(false); // このコンポーネントは、初回のレンダリング時点でユーザーに表示される場合と、ユーザーが下にスクロールすることで表示される場合があります。

  const isSmallScreen = width <= 830;
  const isSmallerScreen = width <= 400;
  const isHovered = realIsHovered;

  const onBlockAddClicked = useCallback((position: AddPosition) => {
    handleBlockAddClicked(position);
  }, [handleBlockAddClicked]);

  type AnimationType =
    | 'fade-in'
    | 'slide-up'
    | 'slide-down'
    | 'slide-left'
    | 'slide-right'
    | 'zoom-in'
    | 'zoom-out'
    | 'gather';

  const animationClasses = useMemo(() => ({
    'fade-in': 'animation-block-fade-in',
    'slide-up': 'animation-block-slide-up',
    'slide-down': 'animation-block-slide-down',
    'slide-left': 'animation-block-slide-left',
    'slide-right': 'animation-block-slide-right',
    'zoom-in': 'animation-block-zoom-in',
    'zoom-out': 'animation-block-zoom-out',
    gather: 'animation-block-gather',
  }), []);

  const getAnimationClass = useCallback((animation: AnimationType | string): string => animationClasses[animation as AnimationType] || '', [animationClasses]);

  useEffect(() => {
    const animationName = getAnimationClass(animation);
    const { current } = realHoverRef;
    if (current) {
      // export後のファイルからアニメーションを処理できるように
      current.setAttribute('animation-for-export', animationName);
    }
  }, [animation, getAnimationClass, realHoverRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        //  entriesは観察された要素の配列です。
        if (entries[0].isIntersecting) {
          // For first loading initial view, do not show animation. Usually initial view ratio is 1.
          setIsInView(entries[0].intersectionRatio < 0.7);
          observer.unobserve(realHoverRef.current);
        }
      },
      {
        root: null, // ビューポートを使用しています。
        threshold: 0.3, // 要素の30%が表示された時にコールバックを実行します。
      },
    );

    const { current } = realHoverRef;

    if (current) {
      observer.observe(current);
    }

    // コンポーネントのアンマウント時にオブザーバーをクリーンアップします。
    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [realHoverRef]);

  return (
    <>
      <div id={dataId} />
      <Stack
        ref={realHoverRef}
        id={`block-${dataId}`}
        sx={{
          position: ((isHeader || isStaticInBackground) && !isEditing) ? 'sticky' : 'relative',
          top: ((isHeader || isStaticInBackground) && !isEditing) ? 0 : null,
          zIndex: (isHeader && !isEditing) ? 5 : null,
          width: isStaticInBackground ? '100%' : 'auto',
        }}
        className={(isInView && !isEditing) ? getAnimationClass(animation) : ''}
      >

        {(isHovered && isEditing) && (
          <Stack>
            <div className={styles.slotRemoveContainer}>
              <Button size="small" variant="contained" color="error" className={styles.slotOptionsButton} onClick={() => handlePanelEvent(dataId, BlockSlotPanelEventType.DELETE)}>
                <ClearOutlined fontSize="small" />
              </Button>
            </div>
            <div className={styles.gapButtonContainer}>
              {/* ブロックの上にある追加ボタン */}
              {!isHeader ? (
                <Button
                  size="small"
                  className={styles.gapButton}
                  variant="contained"
                  onClick={() => {
                    onBlockAddClicked(AddPosition.TOP);
                  }}
                  startIcon={<AddIcon />}
                  style={{
                    width: isSmallerScreen ? '150px' : '180px',
                  }}
                >
                  ブロック追加
                </Button>
              ) : (<></>)}
            </div>
            <Stack direction="row" className={styles.slotOptionsContainer} style={{ borderRadius: 5 }}>
              {isSmallScreen ? (
                <div>
                  <Dropdown>
                    <Dropdown.Toggle
                      id="dropdown-basic"
                      style={{
                        backgroundColor: '#1976D2',
                        borderColor: '#1976D2',
                        color: '#1976D2',
                        minHeight: '31px',
                      }}
                      className="dropdownButton"
                    >
                      <FontAwesomeIcon style={{ cursor: 'pointer', color: 'white', marginLeft: 8 }} icon={faBars} />
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                      style={{
                        width: 'max-content', marginTop: '32px', paddingLeft: '2px', paddingRight: '2px',
                      }}
                    >
                      <BlockSlotActionsButtonGroup
                        showOptionsForLines={showOptionsForLines}
                        showOptionsForAlignment={showOptionsForAlignment}
                        showOptionsForDisplaySettings={showOptionsForDisplaySettings}
                        handleAlignmentOptionsClicked={handleAlignmentOptionsClicked}
                        handleLinesOptionsClicked={handleLinesOptionsClicked}
                        handleDisplaySettingsClicked={handleDisplaySettingsClicked}
                        handleImageIconClicked={handleImageIconClicked}
                        handlePanelEvent={handlePanelEvent}
                        dataId={dataId}
                      />
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              ) : (
                <div className={styles.slotOptionsContainerFlex}>
                  <BlockSlotActionsButtonGroup
                    showOptionsForLines={showOptionsForLines}
                    showOptionsForAlignment={showOptionsForAlignment}
                    showOptionsForDisplaySettings={showOptionsForDisplaySettings}
                    handleAlignmentOptionsClicked={handleAlignmentOptionsClicked}
                    handleLinesOptionsClicked={handleLinesOptionsClicked}
                    handleDisplaySettingsClicked={handleDisplaySettingsClicked}
                    handleImageIconClicked={handleImageIconClicked}
                    handlePanelEvent={handlePanelEvent}
                    dataId={dataId}
                  />
                </div>
              )}
            </Stack>
          </Stack>

        )}

        <Box sx={{ backgroundColor: defaultBackgroundColor }}>
          {children}
        </Box>

        {isHovered && isEditing && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'end',
              justifyContent: 'center',
              fontWeight: 'bold',
              color: 'rgba(0, 0, 0, 0.3)',
              pointerEvents: 'none',
              paddingBottom: '15px',
            }}
          >
            <Box sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              padding: '5px',
              borderRadius: '5px',
              fontSize: '9px',
            }}
            >
              {`ブロックID：${dataId}`}
            </Box>
          </Box>
        )}

        {/* ブロックの下にある追加ボタン */}
        {((isHovered && isEditing)) && (
          <div className={styles.gapButtonContainer}>
            {!isFooter ? (
              <Button
                size="small"
                className={styles.gapButtonBottom}
                variant="contained"
                onClick={() => {
                  onBlockAddClicked(AddPosition.BOTTOM);
                }}
                startIcon={<AddIcon />}
                style={{
                  width: isSmallerScreen ? '150px' : '180px',
                }}
              >
                ブロック追加
              </Button>
            ) : (<></>)}
          </div>
        )}
      </Stack>
    </>
  );
}

export default BlockSlot;
